import { useEffect, useRef, useState } from "react";
import { Pin, Plus, Trash2, ArrowUp, ArrowDown, Sparkles, Search, Loader2 } from "lucide-react";
import api from "../../lib/api";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";

const KIND_META = {
  products: { label: "Related Products", type: "product" },
  categories: { label: "Related Categories", type: "category" },
  brands: { label: "Related Brands", type: "brand" },
};

export default function RelatedContentManager({ entityType, entityId, kind, value = {}, onChange }) {
  const meta = KIND_META[kind];
  const rc = value[kind] || { pinned: [], auto_enabled: true, max: 4 };
  const [names, setNames] = useState({});
  const [q, setQ] = useState("");
  const [targets, setTargets] = useState([]);
  const [searching, setSearching] = useState(false);
  const [sugg, setSugg] = useState(null);
  const timer = useRef(null);

  const setRc = (patch) => onChange({ ...value, [kind]: { ...rc, ...patch } });

  // resolve pinned names
  useEffect(() => {
    (rc.pinned || []).forEach((id) => {
      if (names[id]) return;
      api.get("/admin/seo/link-targets", { params: { type: meta.type, q: "" } }).catch(() => {});
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadSugg = () => {
    if (!entityId) return;
    api.get("/admin/seo/related-suggestions", { params: { entity_type: entityType, entity_id: entityId, kind } })
      .then(({ data }) => {
        setSugg(data.suggestions || []);
        const m = {};
        (data.suggestions || []).forEach((s) => { m[s.target_id] = s.name; });
        setNames((n) => ({ ...n, ...m }));
      }).catch(() => setSugg([]));
  };
  useEffect(() => { loadSugg(); /* eslint-disable-next-line */ }, [entityId, entityType, kind]);

  const doSearch = (val) => {
    setQ(val);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      setSearching(true);
      api.get("/admin/seo/link-targets", { params: { type: meta.type, q: val } })
        .then(({ data }) => {
          setTargets(data.targets || []);
          const m = {}; (data.targets || []).forEach((t) => { m[t.target_id] = t.name; });
          setNames((n) => ({ ...n, ...m }));
        }).catch(() => {}).finally(() => setSearching(false));
    }, 350);
  };

  const addPin = (id, name) => {
    if ((rc.pinned || []).includes(id)) return;
    setNames((n) => ({ ...n, [id]: name }));
    setRc({ pinned: [...(rc.pinned || []), id] });
    setQ(""); setTargets([]);
  };
  const move = (i, dir) => {
    const j = i + dir; const p = [...(rc.pinned || [])];
    if (j < 0 || j >= p.length) return;
    [p[i], p[j]] = [p[j], p[i]]; setRc({ pinned: p });
  };

  return (
    <div className="rounded-lg border border-white/10 bg-white/5 p-3 space-y-3" data-testid={`related-${kind}`}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <Label className="text-sm font-medium text-white/85 flex items-center gap-2"><Pin className="h-4 w-4" /> {meta.label}</Label>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-xs"><Switch checked={rc.auto_enabled !== false} onCheckedChange={(v) => setRc({ auto_enabled: v })} data-testid={`related-auto-${kind}`} /> Auto-fill</label>
          <div className="flex items-center gap-1 text-xs"><span className="text-white/50">Max</span><Input type="number" min={1} max={12} value={rc.max || 4} onChange={(e) => setRc({ max: parseInt(e.target.value) || 4 })} className="w-16 h-7 bg-black/30 border-white/15" data-testid={`related-max-${kind}`} /></div>
        </div>
      </div>
      <p className="text-[11px] text-white/45">Pinned items always show first; auto-fill uses relevant catalogue matches; {kind === "products" ? "same-category fallback fills any gaps." : "empty when none."}</p>

      {/* Pinned */}
      <div className="space-y-1">
        {(rc.pinned || []).map((id, i) => (
          <div key={id} className="flex items-center gap-2 text-sm bg-black/20 rounded-md px-2 py-1.5" data-testid={`pinned-${kind}-${i}`}>
            <Badge variant="outline" className="text-[10px] border-gold/40 text-gold">pinned</Badge>
            <span className="flex-1 truncate text-white/85">{names[id] || id}</span>
            <button type="button" onClick={() => move(i, -1)} className="text-white/40 hover:text-white p-0.5"><ArrowUp className="h-3.5 w-3.5" /></button>
            <button type="button" onClick={() => move(i, 1)} className="text-white/40 hover:text-white p-0.5"><ArrowDown className="h-3.5 w-3.5" /></button>
            <Button type="button" variant="ghost" size="icon" className="h-6 w-6 text-red-400" onClick={() => setRc({ pinned: (rc.pinned || []).filter((x) => x !== id) })}><Trash2 className="h-3.5 w-3.5" /></Button>
          </div>
        ))}
      </div>

      {/* Add pin */}
      <div className="relative">
        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-white/40" />
        <Input value={q} onChange={(e) => doSearch(e.target.value)} placeholder={`Pin a ${meta.type}…`} className="pl-8 bg-black/30 border-white/15 text-sm" data-testid={`related-search-${kind}`} />
        {q && (
          <div className="absolute z-10 mt-1 w-full max-h-44 overflow-y-auto rounded-md border border-white/10 bg-neutral-900 divide-y divide-white/5 shadow-xl">
            {searching ? <p className="p-2 text-xs text-white/40">Searching…</p> :
              targets.length === 0 ? <p className="p-2 text-xs text-white/40">No matches.</p> :
                targets.map((t) => (
                  <button key={t.target_id} type="button" onClick={() => addPin(t.target_id, t.name)} className="w-full text-left px-3 py-2 hover:bg-white/5 text-sm flex items-center justify-between">
                    <span className="text-white/85">{t.name}</span><Plus className="h-3.5 w-3.5 text-gold" />
                  </button>
                ))}
          </div>
        )}
      </div>

      {/* Auto suggestions preview */}
      {rc.auto_enabled !== false && entityId && (
        <div>
          <p className="text-[11px] text-white/50 flex items-center gap-1 mb-1"><Sparkles className="h-3 w-3 text-gold" /> Auto suggestions{sugg === null ? " (loading…)" : ""}</p>
          <div className="flex flex-wrap gap-1.5">
            {(sugg || []).filter((s) => !(rc.pinned || []).includes(s.target_id)).slice(0, 8).map((s) => (
              <button key={s.target_id} type="button" onClick={() => addPin(s.target_id, s.name)} className="text-[11px] rounded-full border border-white/15 text-white/70 hover:border-gold/40 hover:text-gold px-2.5 py-1 flex items-center gap-1" title={(s.reasons || []).join(", ")} data-testid={`related-sugg-${kind}-${s.target_id}`}>
                <Plus className="h-2.5 w-2.5" /> {s.name}
              </button>
            ))}
            {sugg && sugg.length === 0 && <span className="text-[11px] text-white/35">No automatic matches.</span>}
          </div>
        </div>
      )}
    </div>
  );
}
